import {
  Link16,
  OverflowMenuVertical16,
  PresentationFile16,
} from '@carbon/icons-react';
import { isFinite } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ConversationAPI } from 'api/conversations';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody01Placeholder,
  CpBody01Primary,
  CpHeading04Primary,
} from 'components/atoms/customer-portal/typography';
import { Modal as AntModal } from 'components/atoms/modal';
import { ConversationPane } from 'components/organisms/conversations';
import { useGetAllMembersInProject } from 'hooks/use-idb';
import { useUser } from 'hooks/use-user';
import { useGetStatusUpdateDetailsQuery } from 'queries/status-updates';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { CpColorTag } from '../../components/tags/cp-tags';
import { useCustomerPortalHashMap } from '../../utils';
import { SUContent } from './cp-project-updates';

const Modal = styled(AntModal)`
  .ant-modal-body {
    height: 90svh;
    padding: 0;
  }
  .ant-modal-footer {
    display: none;
  }

  /* set overflow hiiden to parent .ant-modal-wrap */
  .ant-modal-wrap,
  & ~,
  & ~ .ant-modal-wrap {
    overflow: hidden;
  }

  @media (max-width: 768px) {
    top: 0;

    .ant-modal-body {
      height: 95svh;
    }
  }

  @media (max-width: 486px) {
    top: var(--cp-spacing-05);
    .ant-modal-body {
      height: 95svh;
    }
  }
`;

const StyledOutsideClickHandler = styled.div`
  height: 100%;
`;

const OuterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const DetailsWrapper = styled.div`
  width: 768px;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: start;
  /* hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  padding-right: var(--spacing-09);
  padding-left: var(--spacing-09);
  padding-bottom: var(--spacing-08);

  @media (max-width: 768px) {
    padding-right: var(--spacing-06);
    padding-left: var(--spacing-06);
    padding-bottom: var(--spacing-06);
  }

  @media (max-width: 480px) {
    padding-right: var(--spacing-05);
    padding-left: var(--spacing-05);
  }

  .status-update__editor {
    max-width: 100%;
    width: 100%;
  }
`;

const ChatWrapper = styled.div`
  width: 350px;
  overflow: hidden;
  height: 100%;
  border-left: 1px solid var(--border-color);
  margin-left: var(--spacing-06);

  @media (max-width: 768px) {
    display: none;
  }

  padding-right: var(--spacing-05);
  padding-left: var(--spacing-05);
  padding-top: var(--spacing-08);
  padding-bottom: var(--spacing-08);

  @media (max-width: 768px) {
    padding-right: var(--spacing-04);
    padding-left: var(--spacing-04);
    padding-top: var(--spacing-06);
    padding-bottom: var(--spacing-06);
  }

  @media (max-width: 480px) {
    padding-right: var(--spacing-03);
    padding-left: var(--spacing-03);
  }

  .rl-cp-chat-pane-message-input {
    margin: 0 !important;
  }
`;

const StatusUpdateNameWrapper = styled.div`
  margin-bottom: var(--cp-spacing-08);
  position: sticky;
  top: 0;
  z-index: 110;
  width: 100%;
  background: white;
  margin-bottom: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-top: var(--spacing-08);

  @media (max-width: 768px) {
    padding-top: var(--spacing-06);
  }
`;

const StatusUpdateName = styled(CpHeading04Primary)`
  cursor: pointer;
  margin-bottom: 0;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 136px);
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  width: 150px;
`;

export const IconWrapper = styled.div`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: var(--border-radius-small);
  &:hover {
    background: var(--cp-background-as-black-opacity-02);
  }
  &:active {
    background: var(--cp-background-as-black-opacity);
  }
`;

const CreatedByWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  ${CpBody01Primary} {
    margin-bottom: 0;
    margin-left: var(--cp-spacing-03);
  }
  border-bottom: 1px solid var(--cp-border-subtle);
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
`;

const CreatedBy = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const ToAndCCWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
  border-bottom: 1px solid var(--cp-border-subtle);
  width: 100%;

  ${CpColorTag} {
    margin-left: var(--cp-spacing-03);
  }
`;

const CCEmailsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-wrap: wrap;
  width: calc(100% - 30px);
`;

const SuContentWrapper = styled.div`
  width: 100%;
  padding-top: var(--cp-spacing-08);
  pointer-events: none;
`;

export const CpProjectUpdatePane: React.FC = () => {
  const { hashMap, removeKey } = useCustomerPortalHashMap();
  const currentUser = useUser();

  const statusUpdateId = useMemo(() => {
    return Number(hashMap?.cp_status_update_drawer);
  }, [hashMap]);

  const onClose = useCallback(() => {
    removeKey('cp_status_update_drawer');
  }, [removeKey]);

  const { data: statusUpdate } = useGetStatusUpdateDetailsQuery(
    ['status_update_id', statusUpdateId],
    {
      enabled: !!statusUpdateId && isFinite(statusUpdateId),
    },
  );
  const { data: usersInProject } = useGetAllMembersInProject({
    projectId: statusUpdate?.project?.projectId,
    isPrivate: false,
  });

  const usersInProjectEmails = useMemo(() => {
    return usersInProject?.map((usr) => usr.emailId);
  }, [usersInProject]);

  const toUserMails = useMemo(() => {
    return (statusUpdate?.toUsers ?? []).map((usr) => usr.emailId);
  }, [statusUpdate]);

  const allUsersPresent = useMemo(() => {
    return usersInProjectEmails.every((mailId) => toUserMails.includes(mailId));
  }, [usersInProjectEmails, toUserMails]);

  const onCopy = useCallback(() => {
    // copy the current url
    navigator.clipboard.writeText(window.location.href);
  }, []);

  return (
    <Modal
      centered
      width="1118px"
      visible={!!statusUpdateId}
      onCancel={onClose}
      footer={<></>}
      getContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
      destroyOnClose
    >
      <StyledOutsideClickHandler>
        {statusUpdate ? (
          <OuterWrapper>
            <DetailsWrapper>
              <StatusUpdateNameWrapper>
                <StatusUpdateName>
                  {statusUpdate.statusUpdateTitle}
                </StatusUpdateName>
                <ActionsWrapper>
                  <IconWrapper>
                    <PresentationFile16 />
                  </IconWrapper>
                  <IconWrapper onClick={onCopy}>
                    <Link16 />
                  </IconWrapper>
                  <IconWrapper>
                    <OverflowMenuVertical16 />
                  </IconWrapper>
                </ActionsWrapper>
              </StatusUpdateNameWrapper>
              <CreatedByWrapper>
                <CreatedBy>
                  <Avatar
                    size="small"
                    src={statusUpdate?.createdBy?.photoUrl}
                    name={fullName(statusUpdate?.createdBy)}
                  />
                  <CpBody01Primary>
                    {fullName(statusUpdate?.createdBy)}
                  </CpBody01Primary>
                </CreatedBy>
                <CpBody01Placeholder>
                  {moment(statusUpdate?.createdAt).format('MMM DD, YYYY')}
                </CpBody01Placeholder>
              </CreatedByWrapper>
              <ToAndCCWrapper>
                <CpBody01Placeholder>
                  <FormattedMessage id="su.email.To" />
                  <FormattedMessage id=":" />
                </CpBody01Placeholder>
                <CCEmailsWrapper>
                  {allUsersPresent ? (
                    <>
                      <CpColorTag color="blue">
                        <FormattedMessage id="su.all.project.members" />
                      </CpColorTag>
                    </>
                  ) : (
                    statusUpdate?.toUsers?.map((to) => (
                      <CpColorTag
                        key={to.emailId}
                        color={getRandomColor(to.emailId)}
                      >
                        {to.emailId}
                      </CpColorTag>
                    ))
                  )}
                </CCEmailsWrapper>
              </ToAndCCWrapper>
              <ToAndCCWrapper>
                <CpBody01Placeholder>
                  <FormattedMessage id="su.email.cc" />
                  <FormattedMessage id=":" />
                </CpBody01Placeholder>
                <CCEmailsWrapper>
                  {statusUpdate?.ccUsers?.map((cc) => (
                    <CpColorTag
                      key={cc.emailId}
                      color={getRandomColor(cc.emailId)}
                    >
                      {cc.emailId}
                    </CpColorTag>
                  ))}
                </CCEmailsWrapper>
              </ToAndCCWrapper>
              <SuContentWrapper>
                <SUContent statusUpdate={statusUpdate} />
              </SuContentWrapper>
            </DetailsWrapper>
            <ChatWrapper>
              <ConversationPane
                commentBoxConfig={{
                  hidePrivate: true,
                  hideSmartfills: true,
                  className: 'rl-cp-chat-pane-message-input',
                }}
                spotlights={[]}
                context={{
                  type: 'DOCUMENT',
                  id: statusUpdate?.document?.documentId,
                }}
                projectId={statusUpdate?.project?.projectId}
                accountId={currentUser.account.accountId}
                apiService={ConversationAPI}
                onStarMessage={() => {}}
              />
            </ChatWrapper>
          </OuterWrapper>
        ) : (
          <></>
        )}
      </StyledOutsideClickHandler>
    </Modal>
  );
};
